"use client";
import { Button } from "@/components/ui/Button";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useKeyPressed } from "@/hooks/useKeyPressed";
import { useOnClickOutside } from "@/hooks/useOnClickOutside";
import { dateFormats } from "@/utils/dateFormatter";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { useLocale } from "next-intl";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import "react-day-picker/style.css";
import { createPortal } from "react-dom";
import DatePickerContent from "./DatePickerContent";

const DateRangePicker = forwardRef(
  (
    {
      id,
      label,
      value,
      onSelect,
      placeholder,
      disabled,
      onPortalStateChange,
      mobileCheckinClick,
      mobileCheckoutClick,
      checkinDate,
      checkoutDate,
      defaultMonth,
      publicHolidays,
      onInputFocus,
      error,
      containerClassName = "",
      scrollToForm,
      onOpen,
      onCancel,
    },
    ref,
  ) => {
    const isMobile = useIsMobile();
    const locale = useLocale();
    const t = useTranslationDetector("hotelSearch");

    // Optimize edilmiş state yönetimi
    const ANIMATION_STATES = useMemo(
      () => ({
        CLOSED: "closed",
        OPENING: "opening",
        OPEN: "open",
        CLOSING: "closing",
      }),
      [],
    );

    const [uiState, setUiState] = useState({
      animationState: ANIMATION_STATES.CLOSED,
      isFocused: false,
      activeTab: "calendar",
    });

    // Derived values
    const isOpen = uiState.animationState !== ANIMATION_STATES.CLOSED;
    const isFocused = uiState.isFocused;
    const activeTab = uiState.activeTab;
    const shouldRenderPortal =
      isMobile &&
      (uiState.animationState === ANIMATION_STATES.OPENING ||
        uiState.animationState === ANIMATION_STATES.OPEN ||
        uiState.animationState === ANIMATION_STATES.CLOSING);
    const isAnimating =
      isMobile && uiState.animationState === ANIMATION_STATES.OPEN;
    const shouldRenderWebDropdown =
      !isMobile &&
      (uiState.animationState === ANIMATION_STATES.OPENING ||
        uiState.animationState === ANIMATION_STATES.OPEN ||
        uiState.animationState === ANIMATION_STATES.CLOSING);
    const isWebDropdownAnimating =
      !isMobile && uiState.animationState === ANIMATION_STATES.OPEN;
    const escapePressed = useKeyPressed("Escape");

    const containerRef = useRef(null);
    const checkinInputRef = useRef(null);
    const checkoutInputRef = useRef(null);
    const blurTimeoutRef = useRef(null);

    useEffect(() => {
      if (isOpen && onOpen) {
        onOpen();
      }
    }, [isOpen]);

    const handleClickOutside = useCallback(() => {
      // Mobilde portal açıksa click outside'ı devre dışı bırak
      if (isMobile && shouldRenderPortal) {
        return;
      }

      // Web'de dropdown açıksa kapat
      if (!isMobile && isOpen) {
        setUiState((prev) => ({
          ...prev,
          animationState: ANIMATION_STATES.CLOSING,
          isFocused: false,
        }));
      }
    }, [isMobile, isOpen, shouldRenderPortal, ANIMATION_STATES]);

    useEffect(() => {
      if (escapePressed && isOpen) {
        setUiState((prev) => ({
          ...prev,
          animationState: ANIMATION_STATES.CLOSING,
          isFocused: false,
        }));
      }
    }, [escapePressed, isOpen, ANIMATION_STATES]);

    // Her zaman ref ver, kontrol handleClickOutside içinde
    useOnClickOutside(containerRef, handleClickOutside);

    // Portal state değişikliklerini parent'a bildir
    useEffect(() => {
      if (onPortalStateChange && isMobile) {
        onPortalStateChange(shouldRenderPortal);
      }
    }, [shouldRenderPortal, isMobile, onPortalStateChange]);

    // Body scroll yönetimini parent'a bırak - artık burada yapma
    // useLockBodyScroll(shouldRenderPortal && isMobile);

    // Tek useEffect ile tüm animasyon kontrolü
    useEffect(() => {
      if (uiState.animationState === ANIMATION_STATES.OPENING) {
        const timer = setTimeout(() => {
          setUiState((prev) => ({
            ...prev,
            animationState: ANIMATION_STATES.OPEN,
          }));
        }, 10);
        return () => clearTimeout(timer);
      } else if (uiState.animationState === ANIMATION_STATES.CLOSING) {
        const timer = setTimeout(
          () => {
            setUiState((prev) => ({
              ...prev,
              animationState: ANIMATION_STATES.CLOSED,
            }));
          },
          isMobile ? 300 : 200,
        );
        return () => clearTimeout(timer);
      }
    }, [uiState.animationState, isMobile, ANIMATION_STATES]);

    const handleDateSelect = useCallback(
      (date, triggerDate) => {
        if (blurTimeoutRef.current) {
          clearTimeout(blurTimeoutRef.current);
          blurTimeoutRef.current = null;
        }
        // Tarih seçimi her zaman parent'a iletilir; mobilde Apply/Çarpı butonları
        // yalnızca değişikliğin korunup korunmayacağını belirler.
        onSelect?.(date, triggerDate);
      },
      [onSelect],
    );

    // Portal close handler
    const handlePortalClose = useCallback(() => {
      setUiState((prev) => ({
        ...prev,
        animationState: ANIMATION_STATES.CLOSING,
        isFocused: false,
      }));
    }, [ANIMATION_STATES]);

    const handleCheckinFocus = useCallback(() => {
      if (!isMobile && scrollToForm) {
        const inputRect = checkinInputRef.current.getBoundingClientRect();
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        const targetPosition = inputRect.top + scrollTop - 200;

        window.scrollTo({
          top: targetPosition,
          behavior: "smooth",
        });
      }

      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }

      onInputFocus?.("checkin");
      setUiState((prev) => ({
        ...prev,
        animationState: ANIMATION_STATES.OPENING,
        isFocused: true,
      }));
    }, [ANIMATION_STATES, onInputFocus, scrollToForm, isMobile]);

    const handleCheckoutFocus = useCallback(() => {
      if (!isMobile && scrollToForm) {
        const inputRect = checkoutInputRef.current.getBoundingClientRect();
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        const targetPosition = inputRect.top + scrollTop - 200;

        window.scrollTo({
          top: targetPosition,
          behavior: "smooth",
        });
      }

      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }

      onInputFocus?.("checkout");
      setUiState((prev) => ({
        ...prev,
        animationState: ANIMATION_STATES.OPENING,
        isFocused: true,
      }));
    }, [ANIMATION_STATES, onInputFocus, scrollToForm, isMobile]);

    // Imperative handle to allow parent components to open/close the datepicker
    // open() desktop'ta scrollToForm mantığını da tetiklesin diye handleCheckinFocus kullanılır.
    useImperativeHandle(
      ref,
      () => ({
        open: () => {
          handleCheckinFocus();
        },
        close: () => {
          setUiState((prev) => ({
            ...prev,
            animationState: ANIMATION_STATES.CLOSING,
            isFocused: false,
          }));
        },
      }),
      [handleCheckinFocus, ANIMATION_STATES.CLOSING],
    );

    // Blur handler'ı memoize et
    const handleBlur = useCallback(() => {
      // Mobilde portal açıksa blur handler'ı devre dışı bırak
      if (isMobile && shouldRenderPortal) {
        return;
      }

      // Web'de blur handler'ı devre dışı bırak, sadece click outside ile kontrol et
      if (!isMobile) {
        return;
      }

      // Delay blur to allow onClick events to complete
      blurTimeoutRef.current = setTimeout(() => {
        setUiState((prev) => ({
          ...prev,
          animationState: ANIMATION_STATES.CLOSING,
          isFocused: false,
        }));
      }, 150);
    }, [isMobile, shouldRenderPortal, ANIMATION_STATES.CLOSING]);
    // const displayValue = dateFormats.dayLongMonthShortDay(value.from);

    // CSS class'ları memoize et
    const containerClasses = useMemo(
      () =>
        clsx(
          "border-primary-weak relative flex h-[68px] w-full items-center border-b duration-300 ease-in-out md:h-[66px] md:rounded-lg md:border-0",
          isFocused && !error && "md:ring-primary md:ring-2",
          error && "rounded-lg ring-2 ring-red-500",
          containerClassName,
        ),
      [isFocused, error, containerClassName],
    );

    const labelClasses = useMemo(
      () =>
        clsx(
          "hidden md:block text-foreground-muted pointer-events-none absolute left-[18px] transition-all duration-300 ease-in-out md:left-5",
          isFocused || value
            ? "top-[11.5px] text-xs"
            : "top-1/2 -translate-y-1/2 text-base",
        ),
      [isFocused, value],
    );

    const inputClasses = useMemo(
      () =>
        clsx(
          "selection:bg-secondary-weak h-full w-full rounded-lg border-0 px-[18px] font-semibold transition-all duration-300 ease-in-out focus:ring-0 focus:outline-none md:px-5",
          // Mobilde label gizli olduğu için ekstra üst padding yok; md+ da label için pt kullan
          isFocused || value
            ? "py-[12px] md:pt-[31.5px] md:pb-[13.5px]"
            : "py-[12px] md:py-[22.5px]",
        ),
      [isFocused, value],
    );

    // Portal container sınıfları - backdrop yok, direkt tam ekran
    const portalContainerClasses = useMemo(() => {
      return clsx(
        "fixed inset-0 z-50 flex h-full flex-col bg-white",
        "transform transition-all duration-300",
        isAnimating ? "translate-y-0 ease-out" : "translate-y-full ease-in",
      );
    }, [isAnimating]);

    // Web dropdown sınıfları
    const webDropdownClasses = useMemo(() => {
      return clsx(
        // Desktop web: input'un altında ortalanmış geniş dropdown
        "shadow-xxl absolute top-full left-1/2 -translate-x-1/2 z-10 mt-2 w-[860px] min-w-[320px] overflow-hidden rounded-lg bg-white",
        "origin-top transform transition-all duration-200 ease-out",
        isWebDropdownAnimating
          ? "translate-y-0 scale-100 opacity-100"
          : "-translate-y-2 scale-95 opacity-0",
      );
    }, [isWebDropdownAnimating]);

    // Render content component
    const renderDatePickerContent = useCallback(() => {
      return (
        <DatePickerContent
          mode="range"
          value={value}
          handleDateSelect={handleDateSelect}
          defaultMonth={defaultMonth}
          checkinDate={checkinDate}
          checkoutDate={checkoutDate}
          publicHolidays={publicHolidays}
        />
      );
    }, [
      value,
      handleDateSelect,
      defaultMonth,
      checkinDate,
      checkoutDate,
      publicHolidays,
    ]);

    return (
      <>
        <div
          className="relative w-full"
          ref={containerRef}
          data-datepicker="true"
        >
          <div
            // className="border-primary-weak flex items-center border-b md:border-0"
            className={containerClasses}
          >
            <div className="relative h-full w-1/2">
              {label && (
                <label htmlFor={id} className={labelClasses}>
                  {t("checkinDate")}
                </label>
              )}
              <input
                ref={checkinInputRef}
                type="text"
                id={`${id}-checkin`}
                readOnly
                value={
                  isMobile && value?.from
                    ? dateFormats.dayShortMonth(value.from, locale)
                    : dateFormats.dayShortMonthShortDayWithComma(value?.from, locale)
                }
                placeholder={placeholder}
                onFocus={handleCheckinFocus}
                onBlur={handleBlur}
                className={inputClasses}
              />
            </div>
            <span className="hidden shrink-0 md:block h-[1px] w-4 bg-border" aria-hidden />
            <span className="shrink-0 px-0.5 text-foreground-muted text-sm md:hidden" aria-hidden>–</span>
            <div className="relative h-full w-1/2">
              {label && (
                <label
                  htmlFor={id}
                  className={clsx(labelClasses, error && "text-red-500")}
                >
                  {t("checkoutDate")}
                </label>
              )}
              <input
                ref={checkoutInputRef}
                type="text"
                id={`${id}-checkout`}
                readOnly
                value={
                  checkoutDate
                    ? isMobile
                      ? dateFormats.dayShortMonth(value?.to, locale)
                      : dateFormats.dayShortMonthShortDayWithComma(value?.to, locale)
                    : ""
                }
                placeholder={placeholder}
                onFocus={handleCheckoutFocus}
                onBlur={handleBlur}
                className={inputClasses}
              />
              {error && (
                <p className="text-danger absolute -mt-[17px] flex items-center gap-1 pl-4 text-xs md:mt-1">
                  <FontAwesomeIcon
                    icon={byPrefixAndName.far["circle-exclamation"]}
                  />
                  <span>{error}</span>
                </p>
              )}
            </div>
          </div>

          {/* Web dropdown */}
          {!isMobile && shouldRenderWebDropdown && (
            <div className={webDropdownClasses}>
              {renderDatePickerContent()}
            </div>
          )}
        </div>

        {/* Mobile portal - tam ekran slide up modal */}
        {shouldRenderPortal &&
          isMobile &&
          createPortal(
            <div className={portalContainerClasses}>
              {/* Header */}
              <div className="flex shrink-0 items-center justify-between px-4 py-4">
                <span className="w-full text-center text-lg font-semibold">
                  {label}
                </span>
                <button
                  onClick={() => {
                    // Çarpı: değişiklikleri koruma, sadece kapat. Parent isterse
                    // onCancel içinde state'i eski değerlere geri alabilir.
                    onCancel?.();
                    handlePortalClose();
                  }}
                  className="self-end"
                >
                  <FontAwesomeIcon
                    icon={byPrefixAndName.far["xmark"]}
                    className="text-2xl"
                  />
                </button>
              </div>

              {/* Content */}
              <div className="min-h-0 flex-1">
                {renderDatePickerContent()}
                <div className="border-primary-weak relative z-[1] shrink-0 border-t bg-white p-4 md:hidden">
                  <Button
                    onClick={handlePortalClose}
                    className={clsx(
                      "w-full",
                      !checkoutDate &&
                        "!bg-foreground-disabled !text-foreground-on-disabled",
                    )}
                    size="lg"
                    disabled={!checkoutDate}
                  >
                    {t("common.apply")}
                  </Button>
                </div>
              </div>
            </div>,
            document.body,
          )}
      </>
    );
  },
);

DateRangePicker.displayName = "DateRangePicker";

export default DateRangePicker;
